import { Icon } from '@iconify/react';
import { Button, Card } from '@nextui-org/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import AgentIcon from 'Src/assets/agent-logo-new.gif';

import api from '@/services/utils/axios';
import { Message, PromptData } from '@/types/agent';
import { ChatMode } from '@/utils/constants';
import { ChatSidepanel } from '../chat-page/components/chat-sidepanel';
import { sendOffer, setupAudioAnalyzer } from './utils/audio-utils';

type ConnectionState = 'disconnected' | 'connecting' | 'connected';

type RealTimeAgentProps = {
  enable: boolean;
  redirectToChat: (messages: Message[], chatMode?: ChatMode) => void;
  selectedPrompt: PromptData | null;
};

export default function RealTimeAgent({
  redirectToChat,
  selectedPrompt,
}: RealTimeAgentProps) {
  const [connection, setConnection] = useState<{
    pc: RTCPeerConnection;
    dc: RTCDataChannel;
  } | null>(null);
  const [connectionState, setConnectionState] =
    useState<ConnectionState>('disconnected');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isSpeaking, setIsSpeaking] = useState<{
    assistant: boolean;
    user: boolean;
  }>({ assistant: false, user: false });
  const [assistantMediaRecorder, setAssistantMediaRecorder] =
    useState<MediaRecorder | null>(null);
  const [isMicEnabled, setIsMicEnabled] = useState(true);
  const userStreamRef = useRef<MediaStream | null>(null);
  const hasInitialized = useRef(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const actionTitle = queryParams.get('action');

  const shouldShowSidepanel = actionTitle === 'User Background';

  const initializeConnection = useCallback(async () => {
    try {
      setConnectionState('connecting');

      const response = await api.post(
        `/agentos/prompts/${selectedPrompt?.id}/create-rtc-session/`,
        {
          voice: 'shimmer',
          model: import.meta.env.VITE_OPENAI_REALTIME_MODEL,
        }
      );
      const { client_secret } = response.data;

      // Step 1: Create RTC session and get key
      const EPHEMERAL_KEY = client_secret;

      // Step 2: Setup WebRTC connection
      const pc = new RTCPeerConnection();
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;

      // Step 3: Handle assistant's audio
      pc.ontrack = (e) => {
        audioEl.srcObject = e.streams[0];
        const assistantRecorder = new MediaRecorder(e.streams[0]);
        setAssistantMediaRecorder(assistantRecorder);
        assistantRecorder.start();

        // Setup assistant audio visualization
        setupAudioAnalyzer(e.streams[0], (average) => {
          setIsSpeaking((prev) => ({ ...prev, assistant: average > 20 }));
        });
      };

      // Step 4: Setup user's audio
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      userStreamRef.current = stream;
      pc.addTrack(stream.getTracks()[0]);

      // Setup user audio visualization
      setupAudioAnalyzer(stream, (average) => {
        setIsSpeaking((prev) => ({ ...prev, user: average > 20 }));
      });

      // Step 5: Setup data channel
      const dc = pc.createDataChannel('oai-events');
      //   setupDataChannelHandlers(dc, setConnectionState, setMessages, messages);
      dc.onmessage = (e) => {
        const event = JSON.parse(e.data);
        console.log(event);

        if (event.type === 'session.created') {
          setConnectionState('connected');
          dc.send(
            JSON.stringify({
              type: 'session.update',
              session: {
                type: 'realtime',
                audio: {
                  input: {
                    format: { type: 'audio/pcm', rate: 24000 },
                    transcription: {
                      model: 'gpt-4o-mini-transcribe',
                    },
                    turn_detection: {
                      type: 'server_vad',
                    },
                  },
                  output: {
                    format: { type: 'audio/pcm', rate: 24000 },
                  },
                },
              },
            })
          );
        }

        if (
          event.type === 'conversation.item.created' &&
          event.item?.role === 'user'
        ) {
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              id: event.item.id,
              text: '',
              isUser: true,
              timestamp: new Date().toISOString(),
            },
          ]);
        }

        if (event.type === 'response.done') {
          const response = event.response;
          if (response.status === 'failed') {
            const details = response.status_details;
            if (details.error.code === 'rate_limit_exceeded') {
              console.log('Rate limit exceeded. Switching to text mode...');
              toast.error(
                'Switching to voice mode due to connection limitations'
              );
            }
            disconnect(ChatMode.VOICE);
          } else if (response.status === 'completed') {
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                id: response.id,
                text: response.output[0]?.content[0]?.transcript || '',
                isUser: false,
                timestamp: new Date().toISOString(),
              },
            ]);
          }
        }

        if (
          event.type === 'conversation.item.input_audio_transcription.completed'
        ) {
          setMessages((prevMessages) => {
            const exists = prevMessages.some((msg) => msg.id === event.item_id);
            if (exists) {
              return prevMessages.map((msg) => {
                if (msg.id === event.item_id) {
                  return { ...msg, text: event.transcript };
                }
                return msg;
              });
            }
            return [
              ...prevMessages,
              {
                id: event.item_id,
                text: event.transcript,
                isUser: true,
                timestamp: new Date().toISOString(),
              },
            ];
          });
        }
      };

      // Step 6: Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      if (!selectedPrompt?.id) {
        throw new Error('Selected prompt ID is required');
      }
      const answer = await sendOffer(offer.sdp, EPHEMERAL_KEY);
      await pc.setRemoteDescription(answer);

      setConnection({ pc, dc });
    } catch (error) {
      console.error('Connection failed:', error);
      setConnectionState('disconnected');
      alert('Failed to connect. Please try again.');
    }
  }, [selectedPrompt, messages]);

  const toggleMic = useCallback(() => {
    if (userStreamRef.current) {
      const audioTrack = userStreamRef.current.getTracks()[0];
      audioTrack.enabled = !isMicEnabled;
      setIsMicEnabled(!isMicEnabled);
    }
  }, [isMicEnabled]);

  const disconnect = useCallback(
    (fallbackMode?: ChatMode) => {
      if (connection) {
        try {
          // Stop user media stream
          if (userStreamRef.current) {
            userStreamRef.current.getTracks().forEach((track) => track.stop());
            userStreamRef.current = null;
          }

          // Reset mic state
          setIsMicEnabled(true);

          // Stop all tracks from user media
          connection.pc.getSenders().forEach((sender) => {
            if (sender.track) {
              sender.track.stop();
            }
          });

          // Stop and cleanup assistant's audio
          if (assistantMediaRecorder) {
            assistantMediaRecorder.stop();
            setAssistantMediaRecorder(null);
          }

          // Stop all audio elements playing assistant's audio
          document.querySelectorAll('audio').forEach((audio) => {
            audio.pause();
            audio.srcObject = null;
          });

          // Close data channel and peer connection
          if (connection.dc.readyState === 'open') {
            connection.dc.close();
          }
          if (connection.pc.connectionState !== 'closed') {
            connection.pc.close();
          }
        } catch (error) {
          console.error('Error during disconnect:', error);
        } finally {
          setConnection(null);
          setConnectionState('disconnected');
          setIsSpeaking({ assistant: false, user: false });

          const filteredMessages = messages?.filter(
            (message) => !(message.isUser && !message.text)
          );

          redirectToChat(filteredMessages, fallbackMode);
          setMessages([]);
        }
      } else {
        redirectToChat([], fallbackMode);
      }
    },
    [connection, assistantMediaRecorder, messages]
  );

  useEffect(() => {
    if (
      selectedPrompt?.id &&
      !['connected', 'connecting'].includes(connectionState) &&
      !hasInitialized.current
    ) {
      hasInitialized.current = true;
      initializeConnection();
    }
  }, [selectedPrompt?.id, connectionState, initializeConnection]);

  return (
    <div className="flex h-full ">
      <div className="flex-1">
        <Card
          radius="none"
          className="h-[80vh] md:h-[85vh] flex flex-col bg-[white] dark:bg-default-100 border-1 shadow-none px-4 rounded"
        >
          <div className="flex-1 flex flex-col h-full pt-5">
            <div className="flex items-center gap-2">
              <Button
                isIconOnly
                variant="light"
                onPress={disconnect}
                aria-label="Go Back"
              >
                <Icon
                  icon="mdi:arrow-left"
                  className="text-neutral-500 dark:text-neutral-400"
                  style={{ fontSize: '20px' }}
                />
              </Button>
              <h2 className="text-md font-bold text-neutral-900 dark:text-neutral-100 py-2">
                {selectedPrompt?.name}
              </h2>
            </div>
            <div className="h-[90%] overflow-y-auto overflow-x-hidden flex justify-center items-center">
              {/* <div className="flex flex-col space-y-4 p-4">
                {messages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </div> */}
              {connectionState === 'connecting' ? (
                <LoadingIndicator />
              ) : (
                connectionState == 'connected' && (
                  <div className="flex flex-col items-center gap-4">
                    {/* Scale down on mobile, full size on desktop - same smooth animation */}
                    <div className="transform scale-[0.6] md:scale-100 origin-center">
                      <div className="relative">
                        {isSpeaking.assistant && (
                          <>
                            <div className="absolute inset-0 -m-1 animate-ping-slow rounded-full bg-blue-500/20"></div>
                            <div className="absolute inset-0 -m-2 animate-ping-slower rounded-full bg-blue-500/10"></div>
                          </>
                        )}
                        <div className="relative">
                          <img src={AgentIcon} alt="Agent Logo" />
                        </div>
                      </div>
                    </div>

                    {/* {isSpeaking.assistant && assistantMediaRecorder && (
                      <div className="flex justify-center">
                        <LiveAudioVisualizer
                          mediaRecorder={assistantMediaRecorder}
                          width={200}
                          height={50}
                          barWidth={15}
                          gap={3}
                          barColor="#3B82F6"
                          backgroundColor="transparent"
                          smoothingTimeConstant={0.6}
                        />
                      </div>
                    )}
                    {isSpeaking.user && !isSpeaking.assistant && (
                      <div className="flex items-center gap-2">
                        <Icon
                          icon="ri:user-voice-line"
                          className="w-5 h-5 text-blue-500"
                        />
                        <span className="text-sm text-gray-600">
                          You are speaking...
                        </span>
                      </div>
                    )} */}
                  </div>
                )
              )}
            </div>

            {connectionState === 'connected' && (
              <div className="p-4 border-t">
                <div className="flex justify-center gap-4">
                  {/* {['connecting', 'disconnected'].includes(connectionState) ? (
                  <Button
                    color="primary"
                    onClick={initializeConnection}
                    isLoading={connectionState === 'connecting'}
                    startContent={
                      <Icon icon="ri:volume-up-line" className="opacity-50" />
                    }
                  >
                    Start Conversation
                  </Button>
                ) : ( */}
                  <div className="flex gap-6">
                    <Button
                      isIconOnly
                      variant="solid"
                      size="lg"
                      radius="lg"
                      onPress={toggleMic}
                      color={isMicEnabled ? 'primary' : 'default'}
                    >
                      <Icon
                        icon={
                          isMicEnabled
                            ? 'streamline:voice-mail'
                            : 'mdi:microphone-off'
                        }
                        width={20}
                      />
                    </Button>
                    <Button
                      isIconOnly
                      variant="solid"
                      size="lg"
                      radius="lg"
                      onPress={disconnect}
                    >
                      <Icon icon="proicons:cancel" width={20} />
                    </Button>
                  </div>
                  {/* )} */}
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
      {shouldShowSidepanel && <ChatSidepanel />}
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-gray-100 flex justify-center items-center">
        <Icon
          icon={message.isUser ? 'ri:user-line' : 'ri:robot-line'}
          className="w-5 h-5 text-gray-600"
        />
      </div>
      <div className="flex-1">
        <div className="font-medium text-sm mb-1">
          {message.isUser ? 'User' : 'Assistant'}
        </div>
        <div className="text-gray-700">{message.text}</div>
      </div>
      <div className="text-xs text-gray-400">
        {new Date(message.timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </div>
    </div>
  );
}

function LoadingIndicator() {
  return (
    <div className="flex flex-col justify-center items-center h-[60%] gap-3">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      <p className="text-neutral-500 text-sm">Initiating chat...</p>
    </div>
  );
}
